import { useState, useEffect } from 'react';
import {
    Users,
    Plus,
    Search,
    Edit,
    Trash2,
    X,
    CheckCircle,
    AlertCircle,
    Building,
    Phone,
    Mail,
    Fingerprint,
    Briefcase,
    UserCheck,
    Loader2,
    RefreshCw,
    ChevronRight
} from 'lucide-react';
import api from '../../services/api';

export default function ClearingAgents() {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        agent_name: '', kra_pin: '', company_name: '', phone: '', email: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => { fetchAgents(); }, []);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/clearing-agents');
            setAgents(data);
        } catch (err) {
            console.error('Failed to load agents');
            setError('Failed to load clearing agents. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate KRA PIN format (basic validation)
        const kraPinRegex = /^[A-Z]\d{9}[A-Z]$/;
        if (!kraPinRegex.test(form.kra_pin.toUpperCase())) {
            setError('KRA PIN must be in format: A123456789B');
            return;
        }

        try {
            if (editingId) {
                await api.put(`/clearing-agents/${editingId}`, form);
                setSuccess('Agent updated successfully!');
            } else {
                await api.post('/clearing-agents', form);
                setSuccess('Agent added successfully!');
            }
            setForm({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
            setShowForm(false);
            setEditingId(null);
            fetchAgents();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save agent');
        }
    };

    const handleEdit = (agent) => {
        setForm({
            agent_name: agent.agent_name,
            kra_pin: agent.kra_pin,
            company_name: agent.company_name || '',
            phone: agent.phone || '',
            email: agent.email || ''
        });
        setEditingId(agent.id);
        setShowForm(true);
        setError('');
        setSuccess('');
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/clearing-agents/${id}`);
            setDeleteConfirm(null);
            fetchAgents();
            setSuccess('Agent removed successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            alert('Failed to remove agent');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
        setError('');
        setSuccess('');
    };

    // Filter agents based on search
    const filteredAgents = agents.filter(agent =>
        agent.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.kra_pin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <SkeletonLoader />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 rounded-lg">
                                <Users className="w-6 h-6 text-[#3a663f] dark:text-green-400" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white">Clearing Agents</h1>
                                <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
                                    {agents.length} agent{agents.length !== 1 ? 's' : ''} in your list
                                </p>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (showForm) {
                                handleCancel();
                            } else {
                                setShowForm(true);
                                setEditingId(null);
                                setForm({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
                                setError('');
                                setSuccess('');
                            }
                        }}
                        className={`inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${showForm
                                ? 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-300 dark:hover:bg-slate-600'
                                : 'bg-[#3a663f] hover:bg-[#2d5231] text-white'
                            }`}
                    >
                        {showForm ? (
                            <>
                                <X className="w-5 h-5" />
                                Cancel
                            </>
                        ) : (
                            <>
                                <Plus className="w-5 h-5" />
                                Add Agent
                            </>
                        )}
                    </button>
                </div>

                {/* Success/Error Messages */}
                {success && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-4 rounded-xl flex items-start gap-3 animate-fadeIn">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm flex-1">{success}</span>
                        <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-xl flex items-start gap-3 animate-fadeIn">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm flex-1">{error}</span>
                        <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Search Bar */}
                {agents.length > 0 && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search agents by name, KRA PIN, or company..."
                            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all shadow-sm"
                        />
                    </div>
                )}

                {/* Form */}
                {showForm && (
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 animate-fadeIn">
                        <div className="flex items-center gap-2 mb-4">
                            <UserCheck className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                            <h3 className="font-bold text-gray-800 dark:text-white">
                                {editingId ? 'Edit Clearing Agent' : 'New Clearing Agent'}
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    Agent Name *
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., John Doe"
                                    value={form.agent_name}
                                    onChange={e => setForm({ ...form, agent_name: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-shadow hover:shadow-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    KRA PIN *
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., A123456789B"
                                    value={form.kra_pin}
                                    onChange={e => setForm({ ...form, kra_pin: e.target.value.toUpperCase() })}
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-shadow hover:shadow-sm font-mono uppercase"
                                    required
                                />
                                <p className="text-xs text-gray-400 mt-1">Format: Letter + 9 digits + Letter (e.g., A123456789B)</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., ABC Clearing Ltd"
                                    value={form.company_name}
                                    onChange={e => setForm({ ...form, company_name: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-shadow hover:shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    Phone
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., +254 712 345 678"
                                    value={form.phone}
                                    onChange={e => setForm({ ...form, phone: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-shadow hover:shadow-sm"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    placeholder="e.g., agent@company.com"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-shadow hover:shadow-sm"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="flex-1 py-2.5 bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                            >
                                <CheckCircle className="w-4 h-4" />
                                {editingId ? 'Update Agent' : 'Save Agent'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Agents List */}
                {agents.length === 0 && !showForm ? (
                    <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700 mb-6">
                            <Users className="w-12 h-12 text-gray-300 dark:text-slate-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">No Clearing Agents</h3>
                        <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto">
                            Add your first clearing agent to start managing KRA PINs for cargo clearance.
                        </p>
                        <button
                            onClick={() => {
                                setShowForm(true);
                                setEditingId(null);
                                setForm({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
                            }}
                            className="mt-6 inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
                        >
                            <Plus className="w-5 h-5" />
                            Add Your First Agent
                        </button>
                    </div>
                ) : filteredAgents.length === 0 && searchTerm ? (
                    <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <Search className="w-12 h-12 text-gray-300 dark:text-slate-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-700 dark:text-slate-300 mb-2">No matching agents</h3>
                        <p className="text-gray-400 dark:text-slate-500 text-sm">
                            No agents found matching "{searchTerm}"
                        </p>
                        <button
                            onClick={() => setSearchTerm('')}
                            className="mt-4 text-[#3a663f] dark:text-green-400 font-medium hover:underline text-sm"
                        >
                            Clear search
                        </button>
                    </div>
                ) : agents.length > 0 && (
                    <div className="space-y-3">
                        {filteredAgents.map((agent) => (
                            <div
                                key={agent.id}
                                className="group bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-lg hover:border-[#3a663f]/30 dark:hover:border-green-400/30 transition-all duration-300"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                            {agent.agent_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-800 dark:text-white text-lg">
                                                {agent.agent_name}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-slate-400">
                                                <span className="flex items-center gap-1.5">
                                                    <Fingerprint className="w-3.5 h-3.5 text-[#3a663f] dark:text-green-400" />
                                                    <span className="font-mono">{agent.kra_pin}</span>
                                                </span>
                                                {agent.company_name && (
                                                    <span className="flex items-center gap-1.5">
                                                        <Building className="w-3.5 h-3.5 text-[#3a663f] dark:text-green-400" />
                                                        {agent.company_name}
                                                    </span>
                                                )}
                                                {agent.phone && (
                                                    <span className="flex items-center gap-1.5">
                                                        <Phone className="w-3.5 h-3.5 text-[#3a663f] dark:text-green-400" />
                                                        {agent.phone}
                                                    </span>
                                                )}
                                                {agent.email && (
                                                    <span className="flex items-center gap-1.5">
                                                        <Mail className="w-3.5 h-3.5 text-[#3a663f] dark:text-green-400" />
                                                        {agent.email}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                            onClick={() => handleEdit(agent)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors font-medium"
                                        >
                                            <Edit className="w-3.5 h-3.5" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(agent.id)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors font-medium"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Remove Clearing Agent?</h3>
                                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                                        This action cannot be undone. Are you sure you want to remove this agent?
                                    </p>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="flex-1 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-400 font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleDelete(deleteConfirm)}
                                        className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors shadow-lg"
                                    >
                                        Remove Agent
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SKELETON LOADER ==========
function SkeletonLoader() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="animate-pulse space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-48" />
                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-32 mt-2" />
                        </div>
                        <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-xl w-32" />
                    </div>
                    <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl w-full" />
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-24 bg-gray-100 dark:bg-slate-700 rounded-xl" />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}